import {
  Autocomplete,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  MenuItem,
  Select,
  TextField,
  Typography,
} from "@mui/material";
import axios from "axios";
import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import ErrorModal from "../../utils/ErrorModal";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { PhoneInput } from "react-international-phone";
import {
  getCountryCallingCode,
  parsePhoneNumber,
} from "react-phone-number-input";
import CountrySelect from "../../utils/countrySelect";
import ThemedSelect from "../../utils/ThemedSelect";

/* ---------- Direction C tokens (styling only) ---------- */
const FONT = "'Plus Jakarta Sans', system-ui, -apple-system, sans-serif";
const MONO = "'IBM Plex Mono', ui-monospace, monospace";
const INK = "#0F1B2A";
const INK_2 = "#3D4C60";
const LINE = "#E4E9EF";
const FIELD_EDGE = "#D5DEE7";
const PRIMARY = "#0B5C8A";
const ERROR = "#C42B24";

const labelStyle = {
  display: "block",
  marginBottom: 6,
  fontFamily: FONT,
  fontSize: 12,
  fontWeight: 600,
  color: INK_2,
};
const inputStyle = (hasError) => ({
  display: "block",
  width: "100%",
  boxSizing: "border-box",
  height: 42,
  padding: "0 12px",
  margin: 0,
  background: "#FFFFFF",
  border: `1px solid ${hasError ? ERROR : FIELD_EDGE}`,
  borderRadius: 10,
  fontFamily: FONT,
  fontSize: 14,
  color: INK,
  outline: "none",
});
const errorStyle = {
  margin: "5px 0 0",
  fontFamily: FONT,
  fontSize: 12,
  fontWeight: 500,
  color: ERROR,
};
const fieldGap = { marginBottom: 16 };
const outlineSx = (hasError) => ({
  "& .MuiOutlinedInput-notchedOutline": {
    border: `1px solid ${hasError ? ERROR : FIELD_EDGE}`,
  },
  "&:hover .MuiOutlinedInput-notchedOutline": {
    border: `1px solid ${hasError ? ERROR : "#B4C6DA"}`,
  },
  "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
    border: `1px solid ${PRIMARY}`,
  },
});
const btnBase = {
  height: 44,
  gap: 0.75,
  borderRadius: "10px !important",
  fontFamily: FONT,
  fontSize: 14,
  fontWeight: 600,
  textTransform: "none",
  boxShadow: "none",
};
const primaryBtnSx = {
  ...btnBase,
  background: `${PRIMARY} !important`,
  color: "#FFFFFF !important",
  border: "0 !important",
  "&:hover": { background: "#084767 !important" },
  "&.Mui-disabled": { opacity: 0.6 },
};
const outlineBtnSx = {
  ...btnBase,
  background: "#FFFFFF !important",
  color: `${INK} !important`,
  border: `1px solid ${FIELD_EDGE} !important`,
  "&:hover": { background: "#F4F7FA !important" },
  "&.Mui-disabled": { opacity: 0.6 },
};
const menuPaperSx = {
  mt: 0.5,
  borderRadius: "12px",
  border: `1px solid ${LINE}`,
  boxShadow: "0 16px 36px -12px rgba(15,27,42,0.24)",
};

const ITEM_HEIGHT = 40;
const ITEM_PADDING_TOP = 0;
const MenuProps = {
  PaperProps: {
    style: {
      maxHeight: ITEM_HEIGHT * 2.5 + ITEM_PADDING_TOP,
      width: 150,
    },
    sx: {
      ...menuPaperSx,
      "& .MuiList-root": { p: 0.5 },
      "& .MuiMenuItem-root": {
        fontFamily: FONT,
        fontSize: 13.5,
        borderRadius: "8px",
        minHeight: 36,
      },
      "& .MuiMenuItem-root.Mui-selected": {
        background: "#EAF2FB",
        color: "#1B4F82",
        fontWeight: 700,
      },
    },
  },
};
const userRoles = [
  // { name: "Viewer Type 1", value: "VT1" },
  // { name: "Viewer Type 2", value: "VT2" },
  { name: "Telecaller", value: "TELECALLER" },
  { name: "Salesperson", value: "SALESPERSON" },
  { name: "Project Head", value: "PROJECT_HEAD" },
  { name: "Marketer", value: "MARKETER" },
  { name: "Admin", value: "ADMIN" },
];

// Admins manage the whole organization rather than a team, so the team pickers
// and default-assignee section do not apply to them.
const isAdminRole = (role) => role === "ADMIN";
const CreateUser = () => {
  const [orgUser, setOrgUser] = useState();
  const user = JSON.parse(localStorage.getItem("user"));
  const orgId = JSON.parse(localStorage.getItem("org"));
  const { orgUserId } = useParams();
  const [errors, setErrors] = useState("");
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [country, setCountry] = useState("IN");
  const [openError, setOpenError] = useState(false);
  const [errorMsg, setErrorMsg] = useState();
  const [teams, setTeams] = useState([]);
  const [createTeamOpen, setCreateTeamOpen] = useState(false);
  const [teamName, setTeamName] = useState("");
  const [teamNameError, setTeamNameError] = useState("");
  const [teamLoading, setTeamLoading] = useState(false);

  useEffect(() => {
    const getOrg = async () => {
      const res = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/organization/get-by-admin?username=${user.username}&orgId=${orgId}`,
        { headers: { Authorization: `Bearer ${user.accessToken}` } },
      );
      if (
        !res.data.orgLogo &&
        !res.data.orgUsername &&
        !res.data.orgShortform
      ) {
        navigate(`/dashboard/org-details`);
        return;
      }
      const fetchTeams = async () => {
        const res = await axios.get(
          `${process.env.REACT_APP_BACKEND_URL}/team/all?orgId=${orgId}`,
          { headers: { Authorization: `Bearer ${user.accessToken}` } },
        );
        setTeams(res.data || []);
      };
      fetchTeams();
    };
    getOrg();
  }, []);

  const handleChange = (e) => {
    setOrgUser({ ...orgUser, [e.target.name]: e.target.value });
    setErrors({ ...errors, [e.target.name]: "" });
  };

  const validateForm = () => {
    const newErrors = {};
    const data = orgUser || {};

    const isEmpty = (value) => !value || value.trim?.() === "";

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    const mobileRegex = /^\d{7,12}$/;

    if (isEmpty(data.name)) newErrors.name = "User Name is required";
    if (isEmpty(data.userRoleName))
      newErrors.userRoleName = "User Role is required";

    if (isEmpty(data.email)) {
      newErrors.email = "User Email ID is required";
    } else if (!emailRegex.test(data.email)) {
      newErrors.email = "Invalid email address";
    }

    // if (isEmpty(data.mobile)) {
    //   newErrors.mobile = "User Mobile Number is required";
    // } else if (!mobileRegex.test(data.mobile)) {
    //   newErrors.mobile = "Invalid mobile number";
    // }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (teamId = null) => {
    if (!validateForm()) {
      setErrorMsg("Please fill in the required fields.");
      setOpenError(true);
      return;
    }
    setLoading(true);
    try {
      await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/org-user`,
        {
          orgId: orgId,
          username: user.username,
          name: orgUser.name,
          email: orgUser.email,
          mobile: orgUser.mobile.replace(/\D/g, ""),
          countryCode: getCountryCallingCode(country),
          userRoleName: orgUser.userRoleName,
          teamId: teamId || orgUser?.teamId || null,
        },
        { headers: { Authorization: `Bearer ${user.accessToken}` } },
      );
      navigate(`/dashboard/users`);
    } catch (error) {
      setOpenError(true);
      setErrorMsg(
        error.response?.data?.Exception ||
          "Something went wrong. Please try again",
      );
    } finally {
      setLoading(false);
    }
  };

  const handleUpdate = async () => {
    if (!validateForm()) return;
    setLoading(true);
    try {
      await axios.put(
        `${process.env.REACT_APP_BACKEND_URL}/org-user?orgUserId=${orgUserId}`,
        {
          orgId: orgId,
          name: orgUser.name,
          username: user.username,
          email: orgUser.email,
          mobile: orgUser.mobile.replace(/\D/g, ""),
          countryCode: getCountryCallingCode(country),
          userRoleName: orgUser.userRoleName,
          teamId: orgUser.teamId || null,
        },
        { headers: { Authorization: `Bearer ${user.accessToken}` } },
      );
      navigate(`/dashboard/users`);
    } catch (error) {
      setOpenError(true);
      setErrorMsg(
        error.response.data.Exception ||
          "Something went wrong. Please try again",
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (orgUserId !== undefined) {
      const getOrgUser = async () => {
        try {
          const res = await axios.get(
            `${process.env.REACT_APP_BACKEND_URL}/org-user/get?orgUserId=${orgUserId}`,
            { headers: { Authorization: `Bearer ${user.accessToken}` } },
          );
          const data = res.data;

          let parsedPhone = null;

          if (data.mobile) {
            parsedPhone = parsePhoneNumber(
              res.data.countryCode != null
                ? res.data.countryCode.includes("+")
                  ? `${res.data.countryCode}${res.data.mobile}`
                  : `+${res.data.countryCode}${res.data.mobile}`
                : `+${res.data.mobile}`,
            );
            data.mobile = parsedPhone ? parsedPhone.nationalNumber : "";
            if (parsedPhone?.country) {
              setCountry(parsedPhone.country);
            }
          }
          setOrgUser(data);
        } catch (error) {
          console.log(error);
        }
      };

      getOrgUser();
    }
  }, []);

  const handleBack = () => {
    navigate(`/dashboard/users`);
  };

  const handleCreateTeamAndUser = async () => {
    if (!teamName.trim()) {
      setTeamNameError("Team name is required");
      return;
    }

    if (!validateForm()) {
      setCreateTeamOpen(false);
      return;
    }

    setTeamLoading(true);

    try {
      const teamRes = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/team`,
        {
          orgId: orgId,
          name: teamName.trim(),
          usernames: [],
          type: null,
        },
        {
          headers: {
            Authorization: `Bearer ${user.accessToken}`,
          },
        },
      );

      const teamId = teamRes.data.teamId;

      if (!teamId) {
        throw new Error("Team ID not received");
      }

      await handleSubmit(teamId);

      setCreateTeamOpen(false);
      navigate("/dashboard/users");
    } catch (error) {
      setTeamNameError(
        error.response?.data?.Exception ||
          error.message ||
          "Something went wrong",
      );
    } finally {
      setTeamLoading(false);
    }
  };

  return (
    <div style={{ fontFamily: FONT, color: INK, padding: "4px 0 24px" }}>
      {loading && <div className="loading-div"></div>}
      <Typography
        variant="h5"
        my={1}
        fontWeight={700}
        textAlign={"center"}
        sx={{ fontFamily: FONT, fontSize: { xs: 24, sm: 27 }, fontWeight: 800, letterSpacing: "-0.028em", color: INK, mb: 2.5 }}
      >
        Enter User Details
      </Typography>
      <div
        className="add-forms"
        style={{
          width: "100%",
          maxWidth: 520,
          margin: "0 auto",
          boxSizing: "border-box",
          background: "#FFFFFF",
          border: `1px solid ${LINE}`,
          borderRadius: 14,
          padding: "20px 20px 18px",
          boxShadow: "none",
        }}
      >
        <Grid container columnSpacing={2} py={0}>
          <Grid size={{ xs: 12 }} style={fieldGap}>
            <label htmlFor="name" style={labelStyle}>Name</label>
            <input
              placeholder="Enter Full Name"
              type="text"
              id="name"
              name="name"
              onChange={handleChange}
              value={orgUser && orgUser.name}
              className={`${errors.name && "input-error"}`}
              style={inputStyle(!!errors.name)}
            />
            {errors.name && <p className="error" style={errorStyle}>{errors.name}</p>}
          </Grid>
          <Grid size={{ xs: 12 }} style={fieldGap}>
            <label htmlFor="email" style={labelStyle}>User Email ID</label>
            <input
              placeholder="admin@company.com"
              type="email"
              id="email"
              name="email"
              onChange={handleChange}
              value={orgUser && orgUser.email}
              className={`${errors.email && "input-error"}`}
              style={inputStyle(!!errors.email)}
            />
            {errors.email && <p className="error" style={errorStyle}>{errors.email}</p>}
          </Grid>
          <Grid size={{ xs: 12 }} style={fieldGap}>
            <label htmlFor="mobile" style={labelStyle}>User Mobile Number</label>
            <div
              className={`phone-container ${errors.mobile ? "input-error" : ""}`}
              style={{
                display: "flex",
                alignItems: "center",
                gap: 0,
                height: 42,
                boxSizing: "border-box",
                background: "#FFFFFF",
                border: `1px solid ${errors.mobile ? ERROR : FIELD_EDGE}`,
                borderRadius: 10,
                overflow: "hidden",
              }}
            >
              <div
                style={{
                  flex: "0 0 auto",
                  display: "flex",
                  alignItems: "center",
                  height: "100%",
                  padding: "0 4px",
                  background: "#F7F9FC",
                  borderRight: `1px solid ${LINE}`,
                }}
              >
                <CountrySelect
                  value={country}
                  onChange={(c) => setCountry(c)}
                />
              </div>
              <input
                type="tel"
                id="mobile"
                name="mobile"
                placeholder="Enter Mobile Number"
                value={orgUser?.mobile || ""}
                onChange={(e) =>
                  setOrgUser((prev) => ({ ...prev, mobile: e.target.value }))
                }
                style={{
                  flex: 1,
                  minWidth: 0,
                  height: "100%",
                  boxSizing: "border-box",
                  padding: "0 12px",
                  margin: 0,
                  border: 0,
                  background: "transparent",
                  fontFamily: MONO,
                  fontSize: 13.5,
                  color: INK,
                  outline: "none",
                }}
              />
            </div>
            {errors.mobile && <p className="error" style={errorStyle}>{errors.mobile}</p>}
          </Grid>
          <Grid size={{ xs: 12 }} style={fieldGap}>
            <label htmlFor="userRoleName" style={labelStyle}>User Role</label>
            <ThemedSelect
              key={orgUser?.userRoleName || "empty"}
              name="userRoleName"
              id="userRoleName"
              onChange={handleChange}
              value={orgUser?.userRoleName || ""}
              error={!!errors?.userRoleName}
              placeholder="Choose"
              options={userRoles.map((u) => ({ value: u.value, label: u.name }))}
            />
            {errors.userRoleName && (
              <p className="error" style={{ ...errorStyle, marginTop: "5px" }}>
                {errors.userRoleName}
              </p>
            )}
          </Grid>
          {teams.length > 0 && !isAdminRole(orgUser?.userRoleName) && (
            <Grid size={{ xs: 12 }} style={fieldGap}>
              <>
                <label style={labelStyle}>
                  Select Team
                  <span style={{ color: ERROR, marginLeft: 2 }}>*</span>
                </label>
                <Autocomplete
                  name="teamId"
                  className={`${errors.teamId && "input-error"}`}
                  options={teams || []}
                  getOptionLabel={(option) => option.name || ""}
                  value={
                    teams?.find((t) => t.teamId === orgUser?.teamId) || null
                  }
                  onChange={(e, value) => {
                    setOrgUser((prev) => ({
                      ...prev,
                      teamId: value?.teamId || "",
                    }));
                  }}
                  isOptionEqualToValue={(option, value) =>
                    option.teamId === value.teamId
                  }
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      name="teamId"
                      placeholder="Select Team"
                      error={!!errors.teamId}
                      helperText={errors.teamId}
                    />
                  )}
                  PaperComponent={({ children, ...props }) => (
                    <Box
                      {...props}
                      sx={{
                        ...menuPaperSx,
                        background: "#fff",
                        overflow: "hidden",
                        "& .MuiAutocomplete-option": {
                          fontFamily: FONT,
                          fontSize: 13.5,
                          borderRadius: "8px",
                          mx: 0.75,
                        },
                      }}
                    >
                      {children}

                      <Box
                        sx={{
                          borderTop: "1px solid #EDF1F5",
                          p: 0.75,
                        }}
                      >
                        <Button
                          fullWidth
                          onMouseDown={(e) => e.preventDefault()}
                          onClick={() => setCreateTeamOpen(true)}
                          sx={{
                            justifyContent: "flex-start",
                            textTransform: "none",
                            fontFamily: FONT,
                            fontSize: "13px",
                            fontWeight: 700,
                            color: PRIMARY,
                            borderRadius: "8px",
                            px: 1.25,
                            "&:hover": { background: "#EAF2FB" },
                          }}
                        >
                          + Create New Team
                        </Button>
                      </Box>
                    </Box>
                  )}
                  sx={{
                    margin: 0,
                    "& .MuiInputBase-root": {
                      minHeight: 42,
                      fontFamily: FONT,
                      fontSize: "14px",
                      borderRadius: "10px",
                      padding: "1px 7px",
                      background: "#FFFFFF",
                    },
                    "& .MuiInputBase-root .MuiOutlinedInput-notchedOutline": {
                      borderColor: errors.teamId ? ERROR : FIELD_EDGE,
                    },
                    "& .MuiInputBase-root:hover .MuiOutlinedInput-notchedOutline": {
                      borderColor: errors.teamId ? ERROR : "#B4C6DA",
                    },
                    "& .MuiInputBase-root.Mui-focused .MuiOutlinedInput-notchedOutline": {
                      borderColor: PRIMARY,
                      borderWidth: 1,
                    },
                    "& .MuiFormHelperText-root": {
                      mx: 0,
                      mt: "5px",
                      fontFamily: FONT,
                      fontSize: 12,
                      color: ERROR,
                    },
                  }}
                />
              </>
            </Grid>
          )}
        </Grid>
        <div style={{ height: 1, background: "#EDF1F5", margin: "4px -20px 16px" }} />
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
          {orgUser && orgUser.orgUserId ? (
            <Button
              className="btn-contained"
              fullWidth
              disabled={loading}
              onClick={handleUpdate}
              sx={primaryBtnSx}
            >
              Update User
            </Button>
          ) : (
            <Button
              className="btn-contained"
              fullWidth
              disabled={loading}
              onClick={() => handleSubmit(null)}
              sx={primaryBtnSx}
            >
              Create New User
            </Button>
          )}
          <Button
            className="btn-outlined"
            fullWidth
            onClick={handleBack}
            sx={{ ...outlineBtnSx, mt: 0 }}
          >
            <ArrowBackIcon sx={{ fontSize: 17 }} /> Back
          </Button>
        </Box>
      </div>

      <Dialog
        open={createTeamOpen}
        onClose={() => {
          if (!teamLoading) {
            setCreateTeamOpen(false);
            setTeamName("");
            setTeamNameError("");
          }
        }}
        fullWidth
        maxWidth="xs"
        PaperProps={{
          sx: {
            borderRadius: "18px",
            fontFamily: FONT,
            boxShadow: "0 32px 64px -20px rgba(15,27,42,0.5)",
          },
        }}
        slotProps={{ backdrop: { sx: { background: "rgba(15,27,42,0.46)" } } }}
      >
        <DialogTitle
          sx={{
            fontFamily: FONT,
            fontSize: "18px",
            fontWeight: 800,
            letterSpacing: "-0.02em",
            color: INK,
            pb: 1,
          }}
        >
          Create New Team
        </DialogTitle>

        <DialogContent>
          <Typography
            sx={{
              fontFamily: FONT,
              fontSize: "12px",
              fontWeight: 600,
              color: INK_2,
              mb: 0.75,
            }}
          >
            Team Name
            <span style={{ color: ERROR, marginLeft: 2 }}>*</span>
          </Typography>

          <TextField
            fullWidth
            autoFocus
            placeholder="Enter Team Name"
            value={teamName}
            onChange={(e) => {
              setTeamName(e.target.value);

              if (teamNameError) {
                setTeamNameError("");
              }
            }}
            error={!!teamNameError}
            helperText={teamNameError}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                handleCreateTeamAndUser();
              }
            }}
            sx={{
              "& .MuiOutlinedInput-root": {
                height: 42,
                fontFamily: FONT,
                fontSize: "14px",
                borderRadius: "10px",
                ...outlineSx(!!teamNameError),
              },
              "& .MuiFormHelperText-root": {
                mx: 0,
                mt: "5px",
                fontFamily: FONT,
                fontSize: 12,
              },
            }}
          />
        </DialogContent>

        <DialogActions sx={{ display: "flex", flexDirection: "column", gap: 1, px: 3, pb: 3, "& > :not(style) ~ :not(style)": { ml: 0 } }}>
          <Button
            className="btn-contained"
            disabled={teamLoading}
            onClick={handleCreateTeamAndUser}
            fullWidth
            sx={primaryBtnSx}
          >
            {teamLoading ? "Creating..." : "Create Team"}
          </Button>
          <Button
            className="btn-outlined"
            disabled={teamLoading}
            onClick={() => {
              setCreateTeamOpen(false);
              setTeamName("");
              setTeamNameError("");
            }}
            fullWidth
            sx={outlineBtnSx}
          >
            Cancel
          </Button>
        </DialogActions>
      </Dialog>

      <ErrorModal
        open={openError}
        handleClose={() => setOpenError(false)}
        msg={errorMsg}
      />
    </div>
  );
};

export default CreateUser;